import { Link } from "react-router-dom";
import { Reveal, SectionLabel, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { ArrowUpRight, Github } from "lucide-react";
import { projects } from "@/data/projects";

const GITHUB_URL = "https://github.com/ra-sys-io";

export default function Projects() {
  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="relative py-24 lg:py-32 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel id="SYS_04">OPS // PROJECTS</SectionLabel>

        <Reveal>
          <h2 className="font-['Unbounded'] font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F5F5F5] leading-[1.05] max-w-3xl">
            Hands-on labs.
            <br />
            <span className="text-[#A1A1AA]">Built to prove</span>{" "}
            <span className="text-[#00FF66]">the paper trail</span>.
          </h2>
        </Reveal>

        <StaggerGroup className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {projects.map((p) => (
            <StaggerItem key={p.id}>
              <Link
                to={`/projects/${p.slug}`}
                data-testid={`project-card-${p.id.toLowerCase()}`}
                className="group relative h-full border border-white/10 bg-[#0A0A0A] flex flex-col overflow-hidden hover:border-[#00FF66] transition-colors"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-90 transition-opacity duration-500"
                    style={{ filter: "grayscale(0.7) contrast(1.1)" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#00FF66] border border-[#00FF66]/40 px-2 py-1 bg-black/60">
                    [{p.id}] // {p.year}
                  </div>
                  <div className="absolute top-4 right-4 font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#F5F5F5]">
                    {p.client}
                  </div>
                </div>

                <div className="p-6 lg:p-8 flex flex-col gap-5 flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-['Unbounded'] font-bold text-2xl lg:text-3xl text-[#F5F5F5] leading-tight group-hover:text-[#00FF66] transition-colors">
                      {p.title}
                    </h3>
                    <ArrowUpRight
                      size={22}
                      strokeWidth={1.5}
                      className="text-[#A1A1AA] group-hover:text-[#00FF66] group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform"
                    />
                  </div>

                  <p className="text-sm text-[#A1A1AA] leading-relaxed">{p.summary}</p>

                  <div className="text-sm text-[#F5F5F5] leading-relaxed">
                    <span className="font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#00FF66] block mb-2">
                      METHODOLOGY
                    </span>
                    <span
                      className="text-[#D4D4D8]"
                      data-testid={`project-redacted-${p.id.toLowerCase()}`}
                    >
                      {p.redacted}
                    </span>
                  </div>

                  <div className="mt-auto pt-4 border-t border-white/10 grid grid-cols-3 gap-4">
                    {p.metrics.map((m) => (
                      <div key={m.v}>
                        <div className="font-['Unbounded'] font-bold text-xl lg:text-2xl text-[#F5F5F5]">
                          {m.k}
                        </div>
                        <div className="font-['JetBrains_Mono'] text-[9px] tracking-[0.3em] text-[#A1A1AA] mt-1">
                          {m.v}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-2 inline-flex items-center gap-2 font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#00FF66] opacity-0 group-hover:opacity-100 transition-opacity">
                    READ_CASE_STUDY →
                  </div>
                </div>
              </Link>
            </StaggerItem>
          ))}

          {/* Companion card — more coming on GitHub */}
          <StaggerItem>
            <a
              href={GITHUB_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="project-more-github"
              className="group relative h-full border border-dashed border-white/15 bg-[#080808] flex flex-col justify-between p-8 lg:p-10 hover:border-[#00FF66] transition-colors"
              style={{ transition: "border-color 0.2s" }}
            >
              <div>
                <div className="font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#A1A1AA] mb-4">
                  [PIPELINE]
                </div>
                <h3 className="font-['Unbounded'] font-bold text-2xl lg:text-3xl leading-tight text-[#F5F5F5] group-hover:text-[#00FF66] transition-colors">
                  More labs in flight.
                </h3>
                <p className="mt-4 text-sm text-[#A1A1AA] leading-relaxed max-w-md">
                  Detection engineering, KQL hunts, DISA STIG audits, and Azure
                  policy work. New writeups shipped as they land — follow along on
                  GitHub.
                </p>
              </div>
              <div className="mt-8 flex items-center justify-between">
                <span className="inline-flex items-center gap-2 font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] text-[#00FF66]">
                  <Github size={14} strokeWidth={1.5} />
                  ra-sys-io
                </span>
                <ArrowUpRight
                  size={22}
                  strokeWidth={1.5}
                  className="text-[#A1A1AA] group-hover:text-[#00FF66] group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform"
                />
              </div>
            </a>
          </StaggerItem>
        </StaggerGroup>
      </div>
    </section>
  );
}
